import { abs } from "mathjs";
import { DATA_TYPES, ONE_HOT_MODE } from "../constants";
import {
  customTopicsDataType,
  datasetDataType,
  textProcessor,
  topicCloudDataType,
} from "../dashboard/NLP/textProcessor";
import { customTopicType } from "../dashboard/PersonaGroupings/CustomTopicTable";
import { isNaN2, safeTrim } from "../helpers/DataHelpers";
import { datasetData } from "../state";
import Column, { colStatsType } from "./Column";
import { gaussianElimination } from "./GaussianElimination";

export type mlDataType = {
  X: number[][];
  y: number[];
  info: infoType;
  colsToDrop: number[];
};

export type infoType = {
  yColumn: string;
  xColIndexes: Map<string, number[]>;
  xColClasses: number[];
  inputShape: number;
  outputShape: number;
  outputType: string;
  weights?: number[];
};

export type dataSplit = {
  X: number[][];
  y: number[];
  xTrain: number[][];
  yTrain: number[];
  xTest: number[][];
  yTest: number[];
  info: infoType;
  colsToDrop: number[];
};

export type visualizationDataType = {
  colName: string;
  customTopicsData: customTopicsDataType;
  topicCloudData: topicCloudDataType;
  posTopicCloudData: topicCloudDataType;
  negTopicCloudData: topicCloudDataType;
};

export default class Dataset {
  data: datasetData;
  nRows: number;
  nCols: number;
  columns: Column[];
  clolumnsToDrop: number[];
  nlpColumns: string[];

  //NLP data for visualization
  visualizationData: visualizationDataType[];

  constructor(
    dataStr: string,
    shuffle: boolean,
    templateDataset?: Dataset,
    customTopics?: customTopicType[][],
    data?: datasetData
  ) {
    if (data !== undefined) {
      this.data = data;
    } else {
      this.data = this.parseCSVString(dataStr);
    }

    if (templateDataset === undefined) {
      this.nlpColumns = this.findNLPColumns();
    } else {
      if (templateDataset.nlpColumns !== undefined) {
        this.nlpColumns = templateDataset.nlpColumns;
        this.visualizationData = templateDataset.visualizationData;
      } else {
        this.nlpColumns = this.findNLPColumns();
      }
    }
    if (this.nlpColumns.length > 0) {
      const { visualizationData, datasetDataAr } = Dataset.makeDatasetNLPData(
        this.data,
        this.nlpColumns,
        customTopics
      );

      this.visualizationData =
        templateDataset?.visualizationData || visualizationData;
      for (let i = 0; i < this.nlpColumns.length; i++) {
        this.data[`Sentiment: ${this.nlpColumns[i]}`] = datasetDataAr[
          i
        ].sentiment.map((v) => v + "");
        let groups = datasetDataAr[i].groups;
        if (groups !== undefined) {
          this.data[`Topics: ${this.nlpColumns[i]}`] = groups;
        }
      }
    }

    let columnNames = Array.from(Object.keys(this.data)).sort();

    this.nRows = this.data[columnNames[0]].length;

    // Set column data
    if (templateDataset === undefined) {
      this.columns = columnNames.map(
        (colName) => new Column(this.data[colName], colName)
      );
      this.nCols = this.columns.length;
    } else {
      this.makeColsFromTemplate(templateDataset);
      for (let nlpCol of this.nlpColumns) {
        const cols = [`Topics: ${nlpCol}`];
        for (let col of cols) {
          if (this.data[col] !== undefined) {
            let ndx = this.columns.map((c) => c.columnName).indexOf(col);
            if (ndx >= 0) {
              this.columns[ndx] = new Column(this.data[col], col);
            } else {
              this.columns.push(new Column(this.data[col], col));
            }
          }
        }
      }
      this.nCols = this.columns.length;
    }
    //Shuffle Data
    if (shuffle) {
      this.shuffleData();
    }
  }

  makeColsFromTemplate(templateDataset: Dataset) {
    //Remove any columns not in the current data
    let templateCols = templateDataset.columns.filter(
      (col) => this.data[col.columnName] !== undefined
    );

    this.columns = templateCols.map(
      (col) => new Column(this.data[col.columnName], col.columnName, col)
    );
    this.clolumnsToDrop = templateDataset.clolumnsToDrop;
  }

  parseCSVString(dataStr: string, strDelimiter?: string) {
    strDelimiter = strDelimiter || ",";
    if (dataStr !== undefined && dataStr.length > 0 && dataStr[0] === ",") {
      dataStr = "<empty>" + dataStr;
    }

    var objPattern = new RegExp(
      "(\\" +
        strDelimiter +
        "|\\r?\\n|\\r|^)" +
        '(?:"([^"]*(?:""[^"]*)*)"|' +
        '([^"\\' +
        strDelimiter +
        "\\r\\n]*))",
      "gi"
    );

    var arrData: string[][] = [[]];

    var arrMatches = null;

    while ((arrMatches = objPattern.exec(dataStr))) {
      var strMatchedDelimiter = arrMatches[1];

      if (strMatchedDelimiter.length && strMatchedDelimiter !== strDelimiter) {
        arrData.push([]);
      }

      var strMatchedValue;

      if (arrMatches[2]) {
        strMatchedValue = arrMatches[2].replace(new RegExp('""', "g"), '"');
      } else {
        strMatchedValue = arrMatches[3];
      }

      arrData[arrData.length - 1].push(strMatchedValue);
    }

    let data = this.array2Dict(arrData);

    const keys = Object.keys(data);
    for (let key of keys) {
      let vals = data[key];
      for (let i = 0; i < vals.length; i++) {
        vals[i] = safeTrim(vals[i]);
      }
      data[key] = vals;
    }

    return data;
  }

  array2Dict(data: string[][]) {
    const cols: string[] = data[0];
    const nCols: number = cols.length;
    const nRows: number = data.length;
    let dict: datasetData = {};
    for (let i = 0; i < nCols; i++) {
      let row: string[] = [];
      for (let j = 1; j < nRows; j++) {
        row.push(data[j][i]);
      }
      dict[cols[i]] = row;
    }
    return dict;
  }
  isNumber = (val: string) => {
    return !isNaN2(+val);
  };

  changeColType(colName: string, colType: string) {
    const index = this.getColumnIndex(colName);
    let col = this.columns[index];
    if (col.availableColumnTypes.includes(colType)) {
      col.columnType = colType;
      col.processData(this.data[colName]);
    }
    this.columns[index] = col;
  }

  getColumnIndex(colName: string) {
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].columnName === colName) {
        return i;
      }
    }
  }
  getColumn(colName: string) {
    return this.columns[this.getColumnIndex(colName)];
  }

  getMLData(
    yColName: string,
    columnsToDrop?: number[],
    maxRows?: number,
    pred = false,
    oneHot = false
  ) {
    let X: number[][] = [...Array(this.nRows)].map(() => []);
    let y: number[] = [];
    let xColNames: string[] = [];
    let xColIndexes: Map<string, number[]> = new Map();
    let xColClasses: number[] = [];

    for (let col of this.columns) {
      //Process the y data
      let normalized = Dataset.normalize(col.vectorizedData, col.colStats);

      if (col.columnName === yColName) {
        y = normalized;
      }

      //Process the X data
      else {
        if (col.columnType === DATA_TYPES.categorical && ONE_HOT_MODE) {
          let x = this.toOneHot(col.vectorizedData, col.nonSparseClasses);
          for (let i = 0; i < x.length; i++) {
            X[i].push(...x[i]);
          }
          let tmp = Array(x[0].length).fill(0);
          xColNames.push(...tmp.map((v, i) => col.columnName));
          xColClasses.push(...tmp.map((v, i) => i));
        } else if (col.columnType !== DATA_TYPES.unusable) {
          for (let i = 0; i < normalized.length; i++) {
            X[i].push(normalized[i]);
          }
          xColNames.push(col.columnName);
          xColClasses.push(-1);
        }
      }
    }
    let yCol = this.getColumn(yColName);
    if (pred) {
      yCol = this.getColumn(this.columns[0].columnName);
    }

    const outputShape = Column.getNClasses(yCol);

    X = this.dropColumns(X, columnsToDrop);
    const dropCols = columnsToDrop || this.clolumnsToDrop;
    if (dropCols !== undefined) {
      xColNames = xColNames.filter((v, i) => !dropCols.includes(i));
      xColClasses = xColClasses.filter((v, i) => !dropCols.includes(i));
    }
    for (let i = 0; i < xColNames.length; i++) {
      if (xColIndexes.has(xColNames[i])) {
        let tmp = xColIndexes.get(xColNames[i]);
        tmp.push(i);
        xColIndexes.set(xColNames[i], tmp);
      } else {
        xColIndexes.set(xColNames[i], [i]);
      }
    }

    if (maxRows !== undefined) {
      let subData = this.subSample(X, y, maxRows);
      X = subData.X;
      y = subData.y;
    }
    let info: infoType = {
      yColumn: yColName,
      xColIndexes: xColIndexes,
      xColClasses: xColClasses,
      inputShape: X[0].length,
      outputShape: outputShape,
      outputType: yCol.columnType,
    };
    let mlData: mlDataType = {
      X: X,
      y: y,
      info: info,
      colsToDrop: this.clolumnsToDrop,
    };
    return mlData;
  }

  static normalize(data: number[], colStats: colStatsType) {
    const max = isNaN2(colStats.max) ? 1 : colStats.max;
    const min = isNaN2(colStats.max) ? 0 : colStats.min;
    const denominator = Math.max(max - min, 1e-4);
    return data.map((val) => (val - min) / denominator);
  }
  static deNormalize(data: number[], colStats: colStatsType) {
    const max = isNaN2(colStats.max) ? 1 : colStats.max;
    const min = isNaN2(colStats.max) ? 0 : colStats.min;
    const denominator = Math.max(max - min, 1e-4);
    return data.map((val) => val * denominator + min);
  }
  arrayToModelInput(data: Object, yColName: string) {
    let inputCols = this.columns.filter((col) => col.columnName != yColName);
    let row: number[] = [];
    for (let col of inputCols) {
      let inVal = data[col.columnName];
      if (col.columnType === DATA_TYPES.number) {
        var val = Dataset.normalize([+inVal], col.colStats);
        row.push(...val);
      } else if (col.columnType === DATA_TYPES.categorical) {
        var val = Dataset.normalize([col.getClassIndex(inVal)], col.colStats);
        let x = this.toOneHot([+val], col.nonSparseClasses);
        for (let i = 0; i < x.length; i++) {
          row.push(...x[i]);
        }
      } else {
        continue;
      }
    }
    return this.dropColumns([row], this.clolumnsToDrop);
  }
  subSample(X: number[][], y: number[], maxRows: number) {
    if (X.length <= maxRows) {
      return { X: X, y: y };
    } else {
      return { X: X.slice(0, maxRows), y: y.slice(0, maxRows) };
    }
  }
  toOneHot(data: number[], nonSparseClasses: string[]) {
    const nClasses = nonSparseClasses.length + 1;
    let oneHotMatrix: number[][] = [];
    for (let val of data) {
      let oneHot = Array(nClasses).fill(0);
      oneHot[val] = 1;
      oneHotMatrix.push(oneHot);
    }
    return oneHotMatrix;
  }

  shuffleData() {
    let indexes = new Array(this.nRows).fill(0).map((val, index) => index);
    indexes = Dataset.shuffle(indexes);
    for (let i = 0; i < this.columns.length; i++) {
      let col = this.columns[i];
      col.vectorizedData = indexes.map(
        (value: number) => col.vectorizedData[value]
      );
      this.columns[i] = col;
    }
  }

  splitTrainData(mlData: mlDataType, testSize: number): dataSplit {
    const testIndex = Math.round(mlData.X.length * testSize);
    let data = {
      X: mlData.X,
      y: mlData.y,
      xTrain: mlData.X.slice(testIndex),
      yTrain: mlData.y.slice(testIndex),
      xTest: mlData.X.slice(0, testIndex),
      yTest: mlData.y.slice(0, testIndex),
      info: mlData.info,
      colsToDrop: mlData.colsToDrop,
    };
    // Make sure to have one example of each category
    if (data.info.outputType === DATA_TYPES.categorical) {
      const trainClasses = new Set(data.yTrain);
      const allClasses = new Set(data.y);
      if (trainClasses.size === allClasses.size) {
        return data;
      } else {
        const missingClasses = new Set(
          data.y.filter((cls) => !trainClasses.has(cls))
        );
        const indexesToAdd = Array.from(missingClasses).map((i) =>
          data.y.indexOf(i)
        );
        for (let index of indexesToAdd) {
          data.xTrain.push(data.X[index].slice());
          data.yTrain.push(data.y[index]);
        }
      }
    }

    return data;
  }

  getColumnType(columnName: string) {
    return this.getColumn(columnName).columnType;
  }

  static shuffle(array: any[]) {
    var currentIndex = array.length,
      randomIndex;

    const seedrandom = require("seedrandom");
    const generator = seedrandom(100);

    // While there remain elements to shuffle...
    while (0 !== currentIndex) {
      // Pick a remaining element...
      randomIndex = Math.floor(generator() * currentIndex);
      currentIndex--;

      // And swap it with the current element.
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex],
        array[currentIndex],
      ];
    }
    return array;
  }
  dropColumns(X: number[][], colsToDrop?: number[]) {
    if (colsToDrop === undefined) {
      this.calcColsToDrop(X);
      colsToDrop = this.clolumnsToDrop;
    }
    X = X.map((row) =>
      row.filter((_, index) => !new Set(colsToDrop).has(index))
    );

    return X;
  }
  calcColsToDrop(X: number[][]) {
    let X_test = X.slice(0, 500);
    let rref = gaussianElimination(X_test);

    let colsToDrop = new Set(rref[0].map((_, i) => i));
    for (let row of rref) {
      for (let c = 0; c < row.length; c++) {
        if (abs(row[c] - 1) < 1e-5) {
          colsToDrop.delete(c);
          break;
        }
      }
    }
    this.clolumnsToDrop = Array.from(colsToDrop);
  }
  getColFromMatrix(X: number[][], colNumber: number) {
    return X.map((x) => x[colNumber]);
  }

  //NLP
  findNLPColumns() {
    const colNames = Object.keys(this.data);
    let count = 0;
    let empty = 0;
    const nlpColumns: string[] = [];
    for (let colName of colNames) {
      let data = this.data[colName];
      data = Dataset.shuffle([...data]).slice(0, 2000);
      count = 0;
      empty = 0;
      for (let val of data) {
        if (val.length === 0) {
          empty += 1;
          continue;
        }
        if (val.split(" ").length >= 3) {
          count += 1;
        }
      }
      if (empty / data.length >= 0.8) {
        continue;
      } else if (count / (data.length - empty) >= 0.6) {
        let col = new Column(data, colName);
        if (col.columnType === DATA_TYPES.unusable) {
          nlpColumns.push(colName);
        }
      }
    }

    return nlpColumns;
  }

  static makeDatasetNLPData(
    data: Object,
    nlpColumns: string[],
    customTopics: customTopicType[][]
  ) {
    let visualizationData: visualizationDataType[] = [];
    let datasetDataAr: datasetDataType[] = [];

    let ndx = 0;
    for (let col of nlpColumns) {
      let ct = undefined;
      if (customTopics !== undefined && customTopics[ndx] !== undefined) {
        ct = customTopics[ndx];
      }
      const {
        topicCloudData,
        posTopicCloudData,
        negTopicCloudData,
        customTopicsData,
        datasetData,
      } = textProcessor(data[col], ct);
      let vData: visualizationDataType = {
        colName: col,
        customTopicsData: customTopicsData,
        topicCloudData: topicCloudData,
        posTopicCloudData: posTopicCloudData,
        negTopicCloudData: negTopicCloudData,
      };
      visualizationData.push(vData);

      datasetDataAr.push(datasetData);
      ndx += 1;
    }
    return { visualizationData, datasetDataAr };
  }
}
